import React, { useState, useEffect } from 'react';
import { FormProvider, Controller } from 'react-hook-form';
import Select from 'react-select';
import toast from 'react-hot-toast';
import { ReactComponent as RiAlertLine } from '@/assets/icons/ri-alert-line.svg';
import { ReactComponent as RiBadmintonCourt } from '@/assets/icons/ri-badminton-court.svg';
import Input from '@/components/common/Input';
import { currencyFormat } from '@/utils';
import { CircularLoading } from '@/components/common/CircularLoading';
import { getValidCoupon } from '@/store/coupon-detail/action';


const BOOKING_METHODS = [
  {
    id: 1,
    label: 'Walk in'
  },
  {
    id: 3,
    label: 'Call/Chat'
  },
  // {
  //   id: 2,
  //   label: 'Customer App'
  // }
]


const summaryData = (schedules) => {
  const summary = {}
  const hours = {}
  const prices = {}

  schedules.forEach(schedule => {
    const court = schedule.court.id
    const hourPrice: HourPrice = {
      hour: schedule.hour,
      price: schedule.price,
    }
    if (hours[court]) { hours[court].push(schedule.hour) }
    else { hours[court] = [schedule.hour] }
    if (prices[court]) { prices[court].push(hourPrice) }
    else { prices[court] = [hourPrice] }

    summary[court] = {
      fieldName: schedule.court.code,
      hours: hours[court],
      hourPrices: prices[court],
      id: schedule.court.id,
      date: schedule.selectedDate,
      venuePriceId: schedule.court.venue_price_id
    }
  })
  return summary
}

export const groupBookingHoursPrice = (
  hoursPrices: HourPrice[]
): { [key: string]: { start: number; end: number; duration: number, price: number } } => {
  const bookedHours = {};
  hoursPrices.sort((a, b) => a.hour - b.hour);
  let start = 0;
  let end = 0;
  let duration = 0;
  let price = 0;
  hoursPrices.forEach((item) => {
    if (item.hour === end) {
      end += 1;
      duration += 1;
      price += item.price;
      bookedHours[start].end = end;
      bookedHours[start].duration = duration;
      bookedHours[start].price = price;
    } else {
      start = item.hour;
      end = item.hour + 1;
      duration = 1;
      price = item.price;
      bookedHours[start] = {
        start,
        end,
        duration,
        price
      };
    }
  });
  return bookedHours;
};


const calculateVoucherDiscount = (coupon, price) => {
  if (coupon?.type === 'PERCENT') {
    if (price >= coupon.min_price) {
      const totalDiscount = (price * coupon.discount_percentage) / 100
      if (coupon.max_discount_amount > 0) {
        return totalDiscount > coupon.max_discount_amount ? coupon.max_discount_amount : totalDiscount
      } 
      return totalDiscount
    }
  }
  return coupon?.discount_amount || 0
}

const calculateLocalTax = (price: number, taxFee: number) => (price * taxFee) / 100 

const Summary = ({ summaryMethods, selectedSchedules, conflictingSchedules, selectedVenue, selectedCustomer, customers, dispatch }) => {
  const [voucherDiscount, setVoucherDiscount] = useState(0)
  const [couponTimer, setCouponTimer] = useState(null);
  const [isCouponLoading, setIsCouponLoading] = useState(false);
  useEffect(() => {
    summaryMethods.setValue('coupon', '')
    setVoucherDiscount(0)
  }, [selectedVenue])
  let venueCount = 0
  const totalPrice = () => {
    let result = 0
    const details = Object.entries(selectedSchedules)?.map((schedule: {}) => schedule[1]?.map(bookingDetail => {
        const { court, hour, selectedDate, price } = bookingDetail
        return {
          venue_court_id: court.id,
          start_time: hour,
          end_time: hour+1,
          date: selectedDate,
          price
        }
      })
    )
    if (details.flat().length) {
      result = details.flat().map(detail => detail.price)?.reduce((num, total = 0) => total + num) || 0
    }
    return result
  }


  const handleCouponChange = (codeCoupon) =>  {
    if (couponTimer) {
      clearTimeout(couponTimer);
      setCouponTimer(null);
    }
    setIsCouponLoading(true)
    setCouponTimer(
      setTimeout( async () => {
        await dispatch(getValidCoupon({codeMeta: codeCoupon, venueMeta: selectedVenue.id})).then(async (res: {[key: string]: any}) => {
            const foundCoupon = res.payload
            if (res.Error || foundCoupon === undefined) {
              summaryMethods.setValue('coupon', '')
              toast.error("Coupon code not valid")
              
            } else {
              setVoucherDiscount(calculateVoucherDiscount(foundCoupon, totalPrice()))
              summaryMethods.setValue('coupon', foundCoupon.code)
            }
        })
        setIsCouponLoading(false)
      }, 1000)
    );
  }
  const customer = customers.find(cust => cust.id === selectedCustomer?.value);
  return (
    <div className='flex flex-col basis-3/12 bg-white p-0'>
      {!!Object.entries(selectedSchedules).length && <div className='flex items-start px-8 pt-8 pb-0 gap-3 rounded-t'>
        <p className='font-bold text-lg'>Summary</p>
      </div>}
      <div className='flex flex-col items-start p-8 gap-6 rounded-b'>
        {!!conflictingSchedules.length &&
				<div className='flex w-full px-3 py-2.5 gap-2 bg-red-subtle border-l-4 border-red-secondary text-red-secondary'>
					<RiAlertLine/>
					<p className='text-sm text-black-primary w-5/6'>There is a conflicting schedule</p>
				</div>}
        {(customer !== undefined && customer.is_invoice_overdue) &&
				<div className='flex w-full justify-between px-3 py-2.5 gap-2 bg-red-subtle border-l-4 border-red-secondary text-red-secondary'>
					<RiAlertLine/>
					<p className='text-sm text-black-primary w-5/6'>Customer has overdue invoices</p>
				</div>}
        {(customer !== undefined && customer.is_blocked) &&
				<div className='flex w-full justify-between px-3 py-2.5 gap-2 bg-red-subtle border-l-4 border-red-secondary text-red-secondary'>
					<RiAlertLine/>
					<p className='text-sm text-black-primary w-5/6'>Customer blocked due: {customer.blocked_note}</p>
				</div>}
        {Object.keys(selectedSchedules).map(date => {
            const summary = summaryData(selectedSchedules[date]);
            return (!!Object.keys(summary).length &&
							<div className='flex flex-col w-full gap-4'>
								<div className='flex py-3 gap-2 bg-grey-subtle border-b border-grey-line-subtle'>
									<p className='pl-2 font-medium text-xs'>{`${new Date(Number(date)).toLocaleString('id-ID', {
                    day: 'numeric',
                    year: 'numeric',
                    month: 'long',
                  })}`}</p>
								</div>
                {Object.keys(summary).map(court => {
                  venueCount+=1
                  const field = summary[court];
                  return (
                    <div className='flex p-0 gap-4 justify-between items-center'>
                      <div
                        className='flex flex-col justify-center items-center px-3 py-4 gap-2 bg-green-success rounded text-white'>
                        <RiBadmintonCourt />
                      </div>
                      <div className='flex flex-col p-0 w-full'>
                        <p className='font-medium text-sm w-full'>{`${field.fieldName}`}</p>
                        {Object.values(groupBookingHoursPrice(field.hourPrices)).map((item) => {
                          const { start, end, price } = item;
                          
                          return (
                            <div className='flex flex-row'> 
                              <p className='text-xs text-grey-dark w-1/2'>({`${start}.00 - ${end}.00`} WITA)</p>
                              <p
                                className='flex justify-end font-bold text-sm w-1/2'>{
                                  currencyFormat(price)
                                }
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
							</div>
            );
          },
        )}
      </div>
      <div>
        <form className='w-full'>
          <FormProvider {...summaryMethods}>
            <div className='flex flex-col p-7 gap-7 justify-between w-full'>
              <div className='flex flex-col p-0 gap-2 w-full'>
                <p className='font-medium text-sm'>Booking Method</p>
                <Controller
                  name='bookingMethod'
                  render={({ field }) =>
                    <Select
                      name='bookingMethod'
                      options={
                        BOOKING_METHODS.map(method => ({
                          label: method.label,
                          value: method.id,
                        }))
                      }
                      placeholder='Choose Booking Method'
                      {...summaryMethods.register('bookingMethod')}
                      {...field}
                    />
                  }
                />
              </div>
              <div className='flex flex-col p-0 gap-2 w-full'>
                <p className='font-medium text-sm'>Coupon</p>
                <input name='coupon' type='text' placeholder='Type Coupon Code'
                  className="flex w-full items-center py-[8px] px-4 gap-2 border rounded border-solid border-stroke-subtle text-jsm font-normal"
                  onChange={(val) => {
                    handleCouponChange(val.target.value);
                  }}
                  onKeyDown={(val) => {
                    if (val.key.toLowerCase() === "enter") {
                    handleCouponChange(val.currentTarget.value);
                    }
                  }}
                />
              </div>
              <div className='flex flex-col p-0 gap-2 w-full'>
                <p className='font-medium text-sm'>Manual Price</p>
                <Input name='manualPrice' type='number' placeholder='Input'
                className='w-full' {...summaryMethods.register('manualPrice')} />
              </div>
              <div
                className='flex flex-col justify-between items-center gap-1 p-2 rounded-md border border-grey-line-subtle'>
                <p className='font-bold text-sm w-full'>Details</p>
                <div className='flex w-full justify-between items-center py-2'>
                  <p className='text-xs'>Field Booking ({venueCount})</p>
                  <p className='font-medium text-xs'>{currencyFormat(totalPrice())}</p>
                </div>
                <div className='flex w-full justify-between items-center py-2 bg-indigo-500'>
                  <p className='text-xs'>Voucher Code</p>
                  { isCouponLoading ?
                    <CircularLoading colorClass='red-primary' /> :
                    <p className='font-medium text-xs'>- {currencyFormat(voucherDiscount)}</p>
                  }
                </div>
                <div className='flex w-full justify-between items-center py-2 bg-indigo-500'>
                  <p className='text-xs'>Local Tax {selectedVenue?.tax_fee}%</p>
                  { isCouponLoading ?
                    <CircularLoading colorClass='red-primary' /> :
                    <p className='font-medium text-xs'>{currencyFormat(calculateLocalTax(totalPrice() - voucherDiscount, selectedVenue?.tax_fee))}</p>
                  }
                </div>
                <div className='w-full border-b border-grey-line-subtle' />
                <div className='flex w-full justify-between items-center py-2'>
                  <p className='text-xs'>Total Payment</p>
                  <p className='font-medium text-xs'>{currencyFormat(totalPrice() > 0 ?  totalPrice() - voucherDiscount + calculateLocalTax(totalPrice() - voucherDiscount, selectedVenue?.tax_fee) : totalPrice())}</p>
                </div>
              </div>
            </div>
          </FormProvider>
        </form>
      </div>
    </div>
  );
}
export default Summary
